(() => {
  const parseJson = (root, selector, fallback = {}) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || JSON.stringify(fallback));
    } catch (error) {
      console.error('Lane storefront data could not be parsed.', error);
      return fallback;
    }
  };

  const normalize = (value = '') =>
    String(value ?? '')
      .toLocaleLowerCase()
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const initializePreviewImageLoading = () => {
    let imageObserver;

    const loadImages = (root = document) => {
      const images = [];
      if (root.matches?.('img[loading="lazy"]')) images.push(root);
      root.querySelectorAll?.('img[loading="lazy"]').forEach((image) => images.push(image));

      images.forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = 'low';
        image.dataset.lanePreviewPreloaded = 'true';
      });
    };

    const activate = () => {
      const previewBar = document.getElementById('PBarNextFrame');
      const themeEditor = window.Shopify?.designMode === true;
      if ((!previewBar && !themeEditor) || imageObserver) return Boolean(imageObserver);

      loadImages();
      imageObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => loadImages(node)));
      });
      imageObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
      return true;
    };

    if (activate()) return;

    const previewObserver = new MutationObserver(() => {
      if (activate()) previewObserver.disconnect();
    });
    previewObserver.observe(document.documentElement, { childList: true, subtree: true });

    window.addEventListener(
      'load',
      () => {
        if (activate()) {
          previewObserver.disconnect();
          return;
        }

        window.setTimeout(() => previewObserver.disconnect(), 4000);
      },
      { once: true }
    );
  };

  const subscribeToVariantChanges = (element, callback) => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return () => {};

    return subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.variant || String(data.sectionId) !== String(element.dataset.sectionId)) return;
      callback(String(data.variant.id));
    });
  };

  class LaneCardAudio extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.audio = this.querySelector('audio');
      this.label = this.querySelector('[data-lane-audio-label]');
      this.button?.addEventListener('click', () => this.toggle());
      this.audio?.addEventListener('ended', () => this.setPlaying(false));
      this.audio?.addEventListener('pause', () => this.setPlaying(false));
    }

    async toggle() {
      if (!this.audio || !this.button) return;
      if (!this.audio.paused) {
        this.audio.pause();
        return;
      }

      document.querySelectorAll('lane-card-audio audio').forEach((audio) => {
        if (audio !== this.audio) audio.pause();
      });

      try {
        await this.audio.play();
        this.setPlaying(true);
      } catch (error) {
        console.warn('Audio playback could not start.', error);
      }
    }

    setPlaying(playing) {
      this.toggleAttribute('data-playing', playing);
      if (this.label) this.label.textContent = playing ? 'Pause' : 'Listen';
      if (this.button) {
        this.button.setAttribute('aria-label', `${playing ? 'Pause' : 'Listen to'} ${this.dataset.title || 'song'}`);
      }
    }
  }

  class LaneSalesCatalog extends HTMLElement {
    connectedCallback() {
      this.search = this.querySelector('[data-lane-sales-search]');
      this.level = this.querySelector('[data-lane-sales-level]');
      this.voicing = this.querySelector('[data-lane-sales-voicing]');
      this.choir = this.querySelector('[data-lane-sales-choir]');
      this.occasion = this.querySelector('[data-lane-sales-occasion]');
      this.cards = Array.from(this.querySelectorAll('[data-lane-sales-card]'));
      this.letters = Array.from(this.querySelectorAll('[data-lane-sales-letter]'));
      this.count = this.querySelector('[data-lane-sales-count]');
      this.empty = this.querySelector('[data-lane-sales-empty]');
      this.modeButtons = Array.from(this.querySelectorAll('[data-lane-catalog-mode]'));
      this.panels = Array.from(this.querySelectorAll('[data-lane-catalog-panel]'));

      const params = new URLSearchParams(window.location.search);
      this.category = normalize(params.get('category'));
      if (this.search && params.get('q')) this.search.value = params.get('q');
      if (this.level && params.get('level')) this.selectMatching(this.level, params.get('level'));
      if (this.voicing && params.get('voicing')) this.selectMatching(this.voicing, params.get('voicing'));
      if (this.choir && params.get('choir')) this.selectMatching(this.choir, params.get('choir'));
      if (this.occasion && params.get('occasion')) this.selectMatching(this.occasion, params.get('occasion'));

      [this.search, this.level, this.voicing, this.choir, this.occasion].forEach((control) => {
        control?.addEventListener(control === this.search ? 'input' : 'change', () => this.filter());
      });
      this.modeButtons.forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.laneCatalogMode)));

      const categoryViewRequested = params.get('browse') === 'categories' || window.location.hash === '#shop-by-category';
      this.setMode(categoryViewRequested ? 'categories' : 'songs');
      this.filter();
    }

    selectMatching(select, value) {
      const target = normalize(value);
      const option = Array.from(select.options).find(
        (candidate) => normalize(candidate.value) === target || normalize(candidate.textContent) === target
      );
      if (option) select.value = option.value;
    }

    setMode(mode = 'songs') {
      this.modeButtons.forEach((button) => {
        const active = button.dataset.laneCatalogMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.laneCatalogPanel !== mode;
      });
    }

    filter() {
      const query = normalize(this.search?.value);
      const level = normalize(this.level?.value);
      const voicing = normalize(this.voicing?.value);
      const choir = normalize(this.choir?.value);
      const occasion = normalize(this.occasion?.value);
      let visible = 0;

      this.cards.forEach((card) => {
        const queryMatch = !query || normalize(card.dataset.searchText).includes(query);
        const levelMatch = !level || level === 'all levels' || normalize(card.dataset.laneLevel) === level;
        const voicingMatch =
          !voicing || voicing === 'all voicings' || normalize(card.dataset.laneArrangements).includes(voicing);
        const choirMatch = !choir || normalize(card.dataset.laneCategories).includes(choir);
        const occasionMatch = !occasion || normalize(card.dataset.laneCategories).includes(occasion);
        const categoryMatch = !this.category || normalize(card.dataset.laneCategories).includes(this.category);
        const show = queryMatch && levelMatch && voicingMatch && choirMatch && occasionMatch && categoryMatch;

        card.hidden = !show;
        if (show) visible += 1;
      });

      this.letters.forEach((letter) => {
        const value = letter.dataset.laneSalesLetter;
        letter.hidden = !this.cards.some((card) => !card.hidden && card.dataset.letter === value);
      });

      if (this.count) this.count.textContent = `${visible} ${visible === 1 ? 'piece' : 'pieces'}`;
      if (this.empty) this.empty.hidden = visible !== 0;
    }
  }

  class LaneSalesProduct extends HTMLElement {
    connectedCallback() {
      this.variants = parseJson(this, '[data-lane-sales-variants]', []);
      this.summary = this.querySelector('[data-lane-sales-summary]');
      this.mobileSummary = this.querySelector('[data-lane-mobile-summary]');
      this.mobilePrice = this.querySelector('[data-lane-mobile-price]');
      this.mobileButton = this.querySelector('[data-lane-mobile-add]');
      this.headingPrice = this.querySelector('[data-lane-product-price]');
      this.summaryPrice = this.querySelector('[data-lane-sales-summary-price]');
      this.purchaseLabel = this.querySelector('[data-lane-purchase-label]');
      this.purchaseContainer = this.querySelector('[data-lane-shopify-purchase]');
      this.deliveryMessage = this.querySelector('[data-lane-delivery-message]');
      this.mediaExtras = this.querySelector('[data-lane-sales-media-extras]');
      this.productInfo = this.closest('product-info');
      this.mediaSlot = this.productInfo?.querySelector('[data-lane-sales-media-slot]');
      this.mobileCart = this.querySelector('.lane-sales-mobile-cart');
      this.layoutQuery = window.matchMedia('(min-width: 990px)');
      this.handleLayoutChange = () => this.placeResponsiveContent();
      this.unsubscribe = subscribeToVariantChanges(this, (variantId) => this.update(variantId));
      this.mobileButton?.addEventListener('click', () => this.submitCurrent());
      if (this.layoutQuery.addEventListener) this.layoutQuery.addEventListener('change', this.handleLayoutChange);
      else this.layoutQuery.addListener?.(this.handleLayoutChange);
      this.placeResponsiveContent();
      this.update(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      if (this.layoutQuery?.removeEventListener) this.layoutQuery.removeEventListener('change', this.handleLayoutChange);
      else this.layoutQuery?.removeListener?.(this.handleLayoutChange);
    }

    placeMediaExtras() {
      if (!this.mediaExtras) return;
      if (this.layoutQuery?.matches && this.mediaSlot) {
        this.mediaSlot.append(this.mediaExtras);
        return;
      }

      if (this.mobileButton) this.mobileButton.closest('.lane-sales-mobile-cart')?.before(this.mediaExtras);
    }

    placeResponsiveContent() {
      this.placeMediaExtras();
    }

    update(variantId) {
      const variant = this.variants.find((candidate) => String(candidate.id) === String(variantId));
      if (!variant) return;

      const selection =
        variant.options.filter((value) => value && value !== 'Default Title').join(' · ') ||
        (variant.external ? 'Publisher edition' : 'Digital edition');
      if (this.summary) this.summary.textContent = selection;
      if (this.mobileSummary) this.mobileSummary.textContent = selection;
      if (this.mobilePrice) {
        this.mobilePrice.textContent = variant.price;
        this.mobilePrice.hidden = Boolean(variant.external);
      }
      if (this.headingPrice) this.headingPrice.hidden = Boolean(variant.external);
      if (this.summaryPrice) {
        this.summaryPrice.textContent = variant.price;
        this.summaryPrice.hidden = Boolean(variant.external);
      }
      const deliveryBlocked = Boolean(variant.deliveryBlocked) && !variant.external;
      const purchasable = variant.external || (variant.available && !deliveryBlocked);
      if (this.purchaseLabel) {
        this.purchaseLabel.textContent = variant.external
          ? 'Publisher edition'
          : deliveryBlocked
            ? 'Edition unavailable'
            : 'Digital sheet music';
      }
      if (this.deliveryMessage) this.deliveryMessage.hidden = !deliveryBlocked;
      if (this.purchaseContainer) {
        this.purchaseContainer.dataset.laneDeliveryBlocked = String(deliveryBlocked);
        const addButton = this.purchaseContainer.querySelector('button[name="add"]');
        const addButtonText = addButton?.querySelector('span');
        if (addButton) {
          addButton.disabled = !purchasable;
          addButton.setAttribute('aria-disabled', String(!purchasable));
        }
        if (addButtonText) {
          addButtonText.textContent = deliveryBlocked
            ? 'Not available yet'
            : variant.available
              ? 'Add to cart'
              : 'Unavailable';
        }
      }
      if (this.mobileButton) {
        this.mobileButton.disabled = !purchasable;
        this.mobileButton.textContent = variant.external
          ? 'Buy from publisher'
          : deliveryBlocked
            ? 'Not available yet'
            : variant.available
            ? 'Add to cart'
            : 'Unavailable';
        this.mobileButton.dataset.external = String(Boolean(variant.external));
      }
    }

    submitCurrent() {
      if (this.mobileButton?.disabled) return;
      if (this.mobileButton?.dataset.external === 'true') {
        this.querySelector('[data-lane-external-link]')?.click();
        return;
      }

      this.closest('product-info')?.querySelector('[data-lane-shopify-purchase] button[name="add"]')?.click();
    }
  }

  if (!customElements.get('lane-card-audio')) customElements.define('lane-card-audio', LaneCardAudio);
  if (!customElements.get('lane-sales-catalog')) customElements.define('lane-sales-catalog', LaneSalesCatalog);
  if (!customElements.get('lane-sales-product')) customElements.define('lane-sales-product', LaneSalesProduct);
  initializePreviewImageLoading();
})();
